import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { getMultiChoiceButtonSelectOptions } from 'helpers/get-multichoice-button-select-options';

import { BaseContext } from 'context/base.context';
import { RoutePath } from 'routes/route-path.constant';

import { useSaveAnswers } from 'hooks/use-save-answers.hook';
import { useLocalStorage } from 'hooks/use-local-storage.hook';

import { LocalStorageKeys } from 'constants/local-storage';
import { TypographyVariants } from 'constants/typography-variants';

import { Typography } from 'components/typography';
import { NextButton } from 'components/next-button';
import { MultiChoiceButton, MultiChoiceType } from 'components/multichoice-button';
import { FloatedButton } from 'components/floated-button';

export const WorkoutEquipment = () => {
    const { t } = useTranslation();
    const saveAnswers = useSaveAnswers();

    const { pageConfiguration } = useContext(BaseContext);

    const pageKey = LocalStorageKeys[RoutePath.WorkoutEquipment];

    const { pageValue, setPageValue } = useLocalStorage({
        key: pageKey,
        defaultValue: [],
    });

    const { pageValue: userAnswer, setPageValue: setUserAnswer } = useLocalStorage({
        key: `${pageKey}-other-userAnswer`,
        defaultValue: '',
    });

    const multiChoices: MultiChoiceType[] = [
        {
            key: 'none',
            value: t('workout-equipment-option-none')
        },
        {
            key: 'trx',
            value: t('workout-equipment-option-trx')
        },
        {
            key: 'resistance-bands',
            value: t('workout-equipment-option-resistance-bands')
        },
        {
            key: 'barbells',
            value: t('workout-equipment-option-barbells')
        },
        {
            key: 'dumbbells',
            value: t('workout-equipment-option-dumbbells')
        },
        {
            key: 'kettlebells',
            value: t('workout-equipment-option-kettlebells')
        },
        {
            key: 'weight-machines',
            value: t('workout-equipment-option-weight-machines')
        },
        {
            key: 'cable-machines',
            value: t('workout-equipment-option-cable-machines')
        },
        {
            key: 'other',
            value: t('workout-equipment-option-other'),
            userAnswer, setUserAnswer
        },
    ];

    const handleClick = () => {
        const selectOption = getMultiChoiceButtonSelectOptions(pageValue, multiChoices);

        saveAnswers({
            key: pageKey,
            itemQuestion: { question: t('workout-equipment-title'), value: selectOption },
        });

        pageConfiguration.handleOnPageComplete({
            value: pageValue,
            userAnswer: pageValue.includes('other') ? userAnswer.trim() : ''
        });
    };

    return (
        <>
            <Typography variant={TypographyVariants.h1} typographyNext={true}>
                {t('workout-equipment-title')}
            </Typography>

            <Typography variant={TypographyVariants.h2}>
                {t('workout-equipment-subtitle')}
            </Typography>

            {multiChoices.map((v: MultiChoiceType) => (
                <MultiChoiceButton
                    key={v.key}
                    item={v}
                    pageValue={pageValue}
                    setPageValue={setPageValue}
                    disabled={pageValue[0] === 'none' && v.key !== 'none'}
                />
            ))}
            <FloatedButton floated={pageValue.length} withGradient>
                <NextButton onClick={handleClick} disabled={!pageValue.length} />
            </FloatedButton>
        </>
    );
};
